import CloseIcon from '@mui/icons-material/CloseRounded';
import MenuIcon from '@mui/icons-material/MenuRounded';
import GlobalIcon from '@mui/icons-material/PublicRounded';
import SearchIcon from '@mui/icons-material/SearchRounded';
import { Box, ButtonBase, ClickAwayListener, Divider, InputBase, Paper, Typography, createTheme, useMediaQuery, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import ATCOButton from '../../common/ATCOButton';
import { ButtonType } from '../../types/enums';
import { redirectToExternalUrl } from '../../utils/ATCOUtils';
import useResponsiveHelper from '../common/hooks/useResponsiveHelper';
import { OverallHeader } from './Header';
import { ATCOPaletteColor } from '../../theme/ATCOPaletteColor';
import { TypographyVariants } from '../../theme/TypographyVariants';
import Searchbar from '../../common/Searchbar';

interface HeaderIconsProps {
    headerObject?: OverallHeader;
    searchButtonClicked?: boolean;
    setSearchButtonClicked: (searchbuttonClicked: boolean) => void;
    handleGlobalClick: (buttonClickedStatus: boolean) => void;
    globalButtonClicked?: boolean;
    setGlobalButtonClicked: (globalButtonClicked: boolean) => void;
    setMobileMenuClicked: (mobileMenuClicked: boolean) => void;
    setDrawerOpenClicked: (drawerOpenClicked: boolean) => void;
    mobileMenuClicked?: boolean;
}

const useStyles = makeStyles((theme) => ({
    searchBar: {
        display: 'flex',
        height: 55,
        width: '916px',
        [theme.breakpoints.down('tablet')]: {
            width: '382px',
        },
        [theme.breakpoints.only('tablet')]: {
            width: '704px',
        },
    },
    searchText: {
        color: theme.palette.primary.main,
        width: '820px',
        [theme.breakpoints.down('tablet')]: {
            width: '286px',
        },
        [theme.breakpoints.only('tablet')]: {
            width: '608px',
        },
    },
    externalLink: {
        '&:hover': { color: ATCOPaletteColor.white },
        color: ATCOPaletteColor.primary1Tint1,
        marginLeft: '10px',
    },
    searchButton: {
        '&:hover': {
            color: ATCOPaletteColor.primary2Tint4,
        },
    },
    searchIcon: {
        width: '24px',
        height: '24px',
        color: ATCOPaletteColor.shadeBlack,
    },
    textLink: {
        color: ATCOPaletteColor.white,
        textDecoration: 'none',
        '&:hover': {
            color: ATCOPaletteColor.primary2Tint4,
        },
    },
    atcoLogo: {
        height: '32px',
    },
}));

const HeaderIcons: React.FC<HeaderIconsProps> = (props: HeaderIconsProps) => {
    const { headerObject, searchButtonClicked, setSearchButtonClicked, handleGlobalClick, globalButtonClicked, setGlobalButtonClicked, setMobileMenuClicked, setDrawerOpenClicked, mobileMenuClicked } =
        props;
    const theme = useTheme();
    const classes = useStyles();
    const { isDesktop, isTablet, isCustomMobileOrTablet } = useResponsiveHelper();
    const [searchValue, setSearchValue] = useState('');

    const doSearch = (event: React.SyntheticEvent): void => {
        event.preventDefault();
        const searchPagePath = headerObject?.searchUrl;
        if (searchPagePath && searchValue !== '') {
            setSearchButtonClicked(false);
            const searchFullUrl = `${searchPagePath}?query=${searchValue}`;
            redirectToExternalUrl(searchFullUrl, false);
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setSearchButtonClicked(false)}>
            <Box pl={isDesktop ? 2 : isTablet ? 1 : -1} pr={isDesktop ? 2 : isTablet ? 1 : -1} width="100%" display="flex" flexDirection="column">
                <Box display="flex" height="80px" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <a className={classes.atcoLogo} href={headerObject?.siteIconUrl}>
                            <img height="32px" src={headerObject?.siteIcon} alt={headerObject?.alternateTextForSiteIcon} title={headerObject?.alternateTextForSiteIcon} />
                        </a>
                    </Box>
                    <Box display="flex" alignItems="center">
                        {searchButtonClicked && !isCustomMobileOrTablet && headerObject?.searchIcon && (
                            <Box display="flex" width="100%" alignItems="center">
                                <Paper component="form" className={classes.searchBar} sx={{ borderRadius: '100px', marginRight: isDesktop ? '24px' : '16px' }}>
                                    <IconButton
                                        type="button"
                                        aria-label="search"
                                        onClick={(evt) => {
                                            doSearch(evt);
                                        }}
                                    >
                                        <SearchIcon className={classes.searchIcon} />
                                    </IconButton>
                                    <Box display="flex" alignItems="center">
                                        <InputBase
                                            className={classes.searchText}
                                            autoFocus={true}
                                            placeholder="What Are you looking for?"
                                            sx={{ color: `${ATCOPaletteColor.shadeBlack} !important` }}
                                            inputProps={{
                                                onChange: (evt: React.ChangeEvent<HTMLInputElement>) => {
                                                    setSearchValue(evt.target.value);
                                                },
                                                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (event.key === 'Enter') {
                                                        doSearch(event);
                                                    }
                                                },
                                            }}
                                        />
                                    </Box>
                                    <IconButton type="button" aria-label="close" disableRipple onClick={() => setSearchButtonClicked(false)}>
                                        <CloseIcon className={classes.searchIcon} />
                                    </IconButton>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                    <Box display="flex" alignItems="center">
                        {!isCustomMobileOrTablet && !searchButtonClicked && (
                            <a href={headerObject?.firstButtonUrl} className={classes.textLink}>
                                <Typography variant={TypographyVariants.button} fontSize="15px" textTransform="capitalize">
                                    {headerObject?.firstButtonLabel}
                                </Typography>
                            </a>
                        )}
                        {!isCustomMobileOrTablet && !searchButtonClicked && (
                            <a href={headerObject?.secondButtonUrl} className={classes.textLink}>
                                <Typography pl={4} pr={3} variant={TypographyVariants.button} fontSize="15px" textTransform="capitalize">
                                    {headerObject?.secondButtonLabel}
                                </Typography>
                            </a>
                        )}
                        {!isCustomMobileOrTablet && !searchButtonClicked && (
                            <Divider
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    mr: headerObject?.storeLabel ? 3 : 0,
                                    color: ATCOPaletteColor.white,
                                    backgroundColor: 'rgba(255, 255, 255, 0.16)', // 16% transparency
                                }}
                                orientation="vertical"
                                flexItem
                            />
                        )}
                        {!isCustomMobileOrTablet && headerObject?.storeLabel && !searchButtonClicked && (
                            <Box display="flex" alignItems="center">
                                <ATCOButton
                                    type={ButtonType.SECONDARY_DARK}
                                    sx={{
                                        textTransform: 'capitalize',
                                        width: `${((headerObject.storeLabel.length * 8.75 + 74) / 16).toString()}rem`,
                                    }}
                                    text={headerObject.storeLabel}
                                    altText={headerObject.alternateTextForStoreUrl}
                                    urlExternal={headerObject.externalLink}
                                    url={headerObject.storeUrl}
                                />
                            </Box>
                        )}
                        {!isCustomMobileOrTablet && headerObject?.storeLabel && !searchButtonClicked && (
                            <Divider
                                sx={{
                                    ml: 3,
                                    mt: 2,
                                    mb: 2,
                                    color: ATCOPaletteColor.white,
                                    backgroundColor: 'rgba(255, 255, 255, 0.16)', // 16% transparency
                                }}
                                orientation="vertical"
                                flexItem
                            />
                        )}
                        {(isCustomMobileOrTablet || (!isCustomMobileOrTablet && !searchButtonClicked)) && headerObject?.searchIcon && (
                            <IconButton
                                onClick={() => {
                                    setSearchButtonClicked(!searchButtonClicked);
                                    setDrawerOpenClicked(false);
                                }}
                                aria-label={headerObject.alternateTextForSearchIcon}
                                sx={{
                                    marginRight: isDesktop ? '24px' : '16px',
                                    marginLeft: isDesktop ? '24px' : '16px',
                                    color: ATCOPaletteColor.white,
                                    '&:hover': {
                                        color: ATCOPaletteColor.primary2Tint4,
                                    },
                                }}
                            >
                                <SearchIcon />
                            </IconButton>
                        )}
                        {headerObject?.siteNavigationIcon && (
                            <Box display="flex" justifyContent="flex-end">
                                <IconButton
                                    onClick={() => {
                                        handleGlobalClick(!globalButtonClicked);
                                        setMobileMenuClicked(false);
                                        setDrawerOpenClicked(false);
                                        setSearchButtonClicked(false);
                                    }}
                                    sx={{
                                        mr: 2,
                                        color: ATCOPaletteColor.white,
                                        '&:hover': {
                                            color: ATCOPaletteColor.primary2Tint4,
                                        },
                                    }}
                                    aria-label={headerObject.alternateTextForSiteNavigationIcon}
                                >
                                    <GlobalIcon />
                                </IconButton>
                            </Box>
                        )}
                        {isCustomMobileOrTablet && (
                            <ButtonBase
                                title="menu"
                                aria-label="menu"
                                style={{ border: 'none' }}
                                onClick={() => {
                                    setMobileMenuClicked(!mobileMenuClicked);
                                    setDrawerOpenClicked(true);
                                    setGlobalButtonClicked(false);
                                    setSearchButtonClicked(false);
                                }}
                            >
                                {mobileMenuClicked ? <CloseIcon sx={{ color: ATCOPaletteColor.white }} titleAccess="menu" /> : <MenuIcon sx={{ color: ATCOPaletteColor.white }} titleAccess="menu" />}
                            </ButtonBase>
                        )}
                    </Box>
                </Box>
                {searchButtonClicked && isCustomMobileOrTablet && headerObject?.searchIcon && <Searchbar doSearch={doSearch} setSearchValue={setSearchValue} placeholder="What Are you looking for?" />}
            </Box>
        </ClickAwayListener>
    );
};

export default HeaderIcons;
